import React from 'react';
import PropTypes from 'prop-types';
import { compose, withHandlers, withProps, withPropsOnChange } from 'recompose';
import { find } from 'lodash';

import { FilterButton } from 'components/search/FilterButton';
import { Checkbox } from 'components/core/Checkbox';
import { Paragraph, Text } from 'components/core/Typography';
import { Section } from 'components/core/Page';
import { ModalLink } from 'components/core/ModalLink';
import { Image } from 'components/core/Image';
import { VehicleUnitSizeGuide } from 'components/unit/VehicleUnitSizeGuide';
import { VehicleAccommodationGuide } from 'components/unit/VehicleAccommodationGuide';
import { FlexRow } from 'components/atoms/Flex';

import {
	unitSizesBySearchType,
	vehicleUnitAccommodations,
	vehicleUnitAmenities,
	vehicleStorageRequirements,
} from 'config/units';
import { encode } from 'querystring';

import { TrackingClient } from '@sparefoot/tracking-client';
import enclosedUnitImage from 'assets/images/unit-icons/enclosed-unit-outlined.svg';

// Keep in mind that two instances of this VehicleSearchFilters component are used on the RailSearchPage. One
// is in the traditional filter modal and another is in the left search rail. To make things more
// complicated, the filter in the modal does not immediately trigger an update in the global store when
// a filter is selected because the user has to press the Apply button. So, we have to keep track of
// some UI state in the recompose HOCs, and keep that in sync with the global store. In addition, we
// have to keep the UI state in sync between the two instances of the this VehicleSearchFilters component.

// All filters exist on the queryParam object and should be mapped here to the prop names on "filterOptions"
// Remember to add the filters to the query param white list in app/lib/search/index.js
const mapQueryFilterKeysToFilterProps = {
	// Each of these can only have one selected value
	minLength: 'parkingUnitSizes',
	vehicleAccommodation: 'vehicleUnitAccommodations',

	// Amenity checkboxes, multiple selection, each is true or false
	...vehicleUnitAmenities.reduce(
		(obj, filter) => ({
			...obj,
			[filter.checkboxQueryParam]: 'vehicleUnitAmenities',
		}),
		{}
	),

	// Vehicle storage requirement checkboxes
	...vehicleStorageRequirements.reduce(
		(obj, filter) => ({
			...obj,
			[filter.checkboxQueryParam]: 'vehicleStorageRequirements',
		}),
		{}
	),
};

export const queryParamFilterKeys = Object.keys(
	mapQueryFilterKeysToFilterProps
);

export function createFilterKeyValueObj(queryParams) {
	const hasQueryParams = !!queryParams;
	return queryParamFilterKeys.reduce((obj, queryParam) => {
		let queryParamValue =
			hasQueryParams && queryParams[queryParam]
				? queryParams[queryParam]
				: false;
		if (queryParamValue === 'true') {
			queryParamValue = true; // Checkboxes need boolean type
		}
		return { ...obj, [queryParam]: queryParamValue };
	}, {});
}

// minLength needs a corresponding maxLength filter to also be applied to queryParams
// The maxLength filter has special logic sprinkled around
function getMinLengthsMaxValue(filterOptions, newFilterValues) {
	const option = find(
		filterOptions.parkingUnitSizes,
		(item) => item.value === newFilterValues.minLength
	);

	if (option) {
		return option.maxLength;
	}

	return '1000';
}

// function to convert unit.value of 15 for example, to over15Long key in facetCounts
// Used for the size filters on all vehicle pages
export function convertSizeValueToLookUpKey(value) {
	return `over${value}Long`;
}

// function to use value from vehicleAccommodation filters
// to look up their value for availability in facets
export function makeAccommodationLookupKey(label) {
	if (label.split('-').length > 1) {
		return label.split('-')[1];
	}

	return label;
}

const VEHICLE_SIZE_LOOKUP = 'vehicleSize';
const VEHICLE_ACCOMMODATIONS_LOOKUP = 'vehicleAccommodations';
const VEHICLE_REQUIREMENTS_LOOKUP = 'vehicleRequirements';
const VEHICLE_AMENITIES_LOOKUP = 'vehicleAmenities';

export const enhance = compose(
	withProps({
		// Set the static prop filter options once
		filterOptions: {
			parkingUnitSizes: unitSizesBySearchType.parking,
			vehicleUnitAccommodations,
			vehicleUnitAmenities,
			vehicleStorageRequirements,
		},
	}),
	withPropsOnChange(['queryParams'], ({ queryParams }) => ({
		chosenFilterValues: createFilterKeyValueObj(queryParams),
	})),
	withHandlers({
		handleFilter:
			({ onFilter, filterOptions, chosenFilterValues }) =>
			(queryParam, value) => {
				const newValues = {
					...chosenFilterValues,
					[queryParam]: value,
				};

				// This is special logic only for the minLength filter
				if (newValues.minLength) {
					newValues.maxLength = getMinLengthsMaxValue(
						filterOptions,
						newValues
					);
				} else if (newValues.minLength === null) {
					newValues.maxLength = null;
				}

				// When filter is set to true, and wasn't previously send tracking event
				if (value && !chosenFilterValues[queryParam]) {
					let tempSearchFilters = { ...newValues };
					// only pass true params to search_filters arg
					Object.keys(tempSearchFilters).map((key) => {
						if (!tempSearchFilters[key]) {
							delete tempSearchFilters[key];
						}
					});

					// Sends tracking values for which filter was applied
					TrackingClient.trackSearchFiltered({
						search_filters: encode(tempSearchFilters),
						search_label: `${queryParam}-${value}`,
						search_term: queryParam,
					});
				}

				onFilter(newValues);
			},
	}),
	withHandlers({
		handleInputChange: (props) => (checkboxQueryParam) => (value) =>
			props.handleFilter(checkboxQueryParam, value),
	}),
	withHandlers({
		checkFacetCountsForAvailability:
			() => (facetCounts, lookupType, unitValue) => {
				// Formats unit values used for filtering to match facetCount titles
				// Uses constants from above to match filter type to facet count type
				const lookupMap = {
					[VEHICLE_SIZE_LOOKUP]: convertSizeValueToLookUpKey, // size filters
					[VEHICLE_ACCOMMODATIONS_LOOKUP]: makeAccommodationLookupKey, // vehicle unit accommodations | indoor | uncovered etc
					[VEHICLE_REQUIREMENTS_LOOKUP]: (value) => value, // No transformation needed for requirements | title, insurance etc
					[VEHICLE_AMENITIES_LOOKUP]: (value) => value, // No transformation needed for amenities | 24-hour access, drive-up etc
				};

				const facetName = lookupMap[lookupType](unitValue);

				// Vehicle requirements facets are objects like preFilterTypeCounts, rather than string like other facets.
				// These filters should reference the negative values since filters indicate facilities/units that do not require these items
				// Facets are vehicleMustBeRunning | vehicleRequiresInsurance | vehicleRequiresRegistration | vehicleRequiresTitle
				// Last part of ternary is the rest of facet lookups
				const isZero =
					lookupType === VEHICLE_REQUIREMENTS_LOOKUP
						? facetCounts?.[facetName]?.['0'] === 0
						: facetCounts[facetName] === 0;

				const isInFacets =
					JSON.stringify(facetCounts).includes(facetName);

				const shouldDisableButton = isInFacets && isZero;

				return shouldDisableButton;
			},
	})
);

export function VehicleSearchFilters({
	handleFilter,
	handleInputChange,
	chosenFilterValues,
	filterOptions,
	facetCounts,
	checkFacetCountsForAvailability,
	useNewSearch,
}) {
	const vehicleFilterToFacetName = {
		vehicleDrivableNotRequired: 'vehicleMustBeRunning',
		vehicleRegistrationNotRequired: 'vehicleRequiresRegistration',
		vehicleInsuranceNotRequired: 'vehicleRequiresInsurance',
		vehicleTitleNotRequired: 'vehicleRequiresTitle',
		hasSpecial: 'promo',
	};

	// Old search page UI with FilterButtons
	if (!useNewSearch) {
		return (
			<div className="filter-content vehicle-filter-content">
				<Section className="filter-section">
					<Paragraph eyebrow>Unit Size</Paragraph>
					<ul className="filter-group">
						{filterOptions.parkingUnitSizes.map((unit) => {
							const isChecked =
								unit.value === chosenFilterValues.minLength;

							const noCountAvailable =
								checkFacetCountsForAvailability(
									facetCounts,
									VEHICLE_SIZE_LOOKUP,
									unit.value
								);
							const shouldDisableButton =
								noCountAvailable && !isChecked;

							return (
								<li
									key={unit.value}
									className="filter-item"
								>
									<FilterButton
										label={unit.label}
										value={unit.value}
										icon={unit.icon}
										type="minLength"
										selected={isChecked}
										onClick={handleFilter}
										disabled={shouldDisableButton}
									/>
								</li>
							);
						})}
					</ul>
					<ModalLink
						text="View our storage unit size guide"
						content={<VehicleUnitSizeGuide />}
					/>
				</Section>
				<Section className="filter-section">
					<Paragraph eyebrow>Vehicle Unit Accommodation</Paragraph>
					<ul className="filter-group">
						{filterOptions.vehicleUnitAccommodations.map((unit) => {
							const isChecked =
								unit.value ===
								chosenFilterValues.vehicleAccommodation;

							const noCountAvailable =
								checkFacetCountsForAvailability(
									facetCounts,
									VEHICLE_ACCOMMODATIONS_LOOKUP,
									unit.value
								);
							const shouldDisableButton =
								noCountAvailable && !isChecked;

							return (
								<li
									key={unit.value}
									className="filter-item"
								>
									<FilterButton
										label={unit.label}
										value={unit.value}
										icon={unit.icon}
										type="vehicleAccommodation"
										selected={isChecked}
										onClick={handleFilter}
										disabled={shouldDisableButton}
									/>
								</li>
							);
						})}
					</ul>
					<ModalLink
						text="View our vehicle accommodation guide"
						content={<VehicleAccommodationGuide />}
					/>
				</Section>
				<Section className="filter-section">
					<Paragraph eyebrow>Vehicle Storage Requirements</Paragraph>
					<ul className="checkbox-group">
						{filterOptions.vehicleStorageRequirements.map(
							(storageReq, index) => {
								const lookupKey = storageReq.checkboxQueryParam;
								const facetName =
									vehicleFilterToFacetName[lookupKey];

								const isChecked = chosenFilterValues[lookupKey];

								const noCountAvailable =
									checkFacetCountsForAvailability(
										facetCounts,
										VEHICLE_REQUIREMENTS_LOOKUP,
										facetName
									);
								const shouldDisableOption =
									noCountAvailable && !isChecked;

								return (
									<li
										key={storageReq.checkboxQueryParam}
										className="checkbox-item"
									>
										<Checkbox
											disabled={shouldDisableOption}
											id={`vehiclestoragereqs-filter-${index}`}
											label={storageReq.label}
											input={{
												value: chosenFilterValues[
													storageReq.checkboxQueryParam
												],
												onChange: handleInputChange(
													storageReq.checkboxQueryParam
												),
											}}
										/>
									</li>
								);
							}
						)}
					</ul>
				</Section>
				<Section className="filter-section">
					<Paragraph eyebrow>Amenities</Paragraph>
					<ul className="checkbox-group">
						{filterOptions.vehicleUnitAmenities.map(
							(amenity, index) => {
								const isChecked =
									chosenFilterValues[amenity.checkboxQueryParam];

								const paramName = amenity.checkboxQueryParam;
								const convertedAmenityTitle =
									vehicleFilterToFacetName[paramName] ||
									paramName;

								const noCountAvailable =
									checkFacetCountsForAvailability(
										facetCounts,
										VEHICLE_AMENITIES_LOOKUP,
										convertedAmenityTitle
									);

								const shouldDisableOption =
									noCountAvailable && !isChecked;

								return (
									<li
										key={amenity.checkboxQueryParam}
										className="checkbox-item"
									>
										<Checkbox
											id={`amenity-filter-${index}`}
											label={amenity.label}
											input={{
												value: chosenFilterValues[
													amenity.checkboxQueryParam
												],
												onChange: handleInputChange(
													amenity.checkboxQueryParam
												),
											}}
											disabled={shouldDisableOption}
										/>
									</li>
								);
							}
						)}
					</ul>
				</Section>
			</div>
		);
	}

	// New search page UI with Checkboxes
	return (
		<div className="filter-content vehicle-filter-content">
			<Section className="filter-section">
				<Paragraph eyebrow>Unit Size</Paragraph>
				<ul className="checkbox-group">
					{filterOptions.parkingUnitSizes.map((unit, index) => {
						const isChecked =
							unit.value === chosenFilterValues.minLength;

						const noCountAvailable =
							checkFacetCountsForAvailability(
								facetCounts,
								VEHICLE_SIZE_LOOKUP,
								unit.value
							);
						const shouldDisableButton =
							noCountAvailable && !isChecked;

						return (
							<li
								key={unit.value}
								className="checkbox-item"
							>
								<Checkbox
									disabled={shouldDisableButton}
									id={`unit-size-filter-${index}`}
									label={
										<FlexRow 
											alignCenter 
											gapTiny
										>
											{unit.label}
											<Text 
												size="tiny"
												color="gray"
												className="size-text"
												weight="light"
												last
											>
												{unit.sizeText}
											</Text>
										</FlexRow>
										}
									input={{
										value: isChecked,
										onChange: (checked) => {
											handleFilter('minLength', checked ? unit.value : null);
										},
									}}
								/>
							</li>
						);
					})}
				</ul>
				<div className="size-guide-modal-link">
					<Image src={enclosedUnitImage} alt="Info" />
					<ModalLink
						text="View our storage unit size guide"
						content={<VehicleUnitSizeGuide />}
					/>

				</div>
			</Section>
			<div className="filter-section">
				<Section className="filter-section">
					<Paragraph eyebrow>Vehicle Unit Accommodation</Paragraph>
					<ul className="checkbox-group">
						{filterOptions.vehicleUnitAccommodations.map((unit, index) => {
							const isChecked =
								unit.value ===
								chosenFilterValues.vehicleAccommodation;

							const noCountAvailable =
								checkFacetCountsForAvailability(
									facetCounts,
									VEHICLE_ACCOMMODATIONS_LOOKUP,
									unit.value
								);
							const shouldDisableButton =
								noCountAvailable && !isChecked;

							return (
								<li
									key={unit.value}
									className="checkbox-item"
								>
									<Checkbox
										disabled={shouldDisableButton}
										id={`vehicle-accommodation-filter-${index}`}
										label={unit.label}
										input={{
											value: isChecked,
											onChange: (checked) => {
												handleFilter('vehicleAccommodation', checked ? unit.value : null);
											},
										}}
									/>
								</li>
							);
						})}
					</ul>
				</Section>
				<Section className="filter-section">
					<Paragraph eyebrow>Vehicle Storage Requirements</Paragraph>
					<ul className="checkbox-group">
						{filterOptions.vehicleStorageRequirements.map(
							(storageReq, index) => {
								const lookupKey = storageReq.checkboxQueryParam;
								const facetName =
									vehicleFilterToFacetName[lookupKey];

								const isChecked = chosenFilterValues[lookupKey];

								const noCountAvailable =
									checkFacetCountsForAvailability(
										facetCounts,
										VEHICLE_REQUIREMENTS_LOOKUP,
										facetName
									);
								const shouldDisableOption =
									noCountAvailable && !isChecked;

								return (
									<li
										key={storageReq.checkboxQueryParam}
										className="checkbox-item"
									>
										<Checkbox
											disabled={shouldDisableOption}
											id={`vehiclestoragereqs-filter-${index}`}
											label={storageReq.label}
											input={{
												value: chosenFilterValues[
													storageReq.checkboxQueryParam
												],
												onChange: handleInputChange(
													storageReq.checkboxQueryParam
												),
											}}
										/>
									</li>
								);
							}
						)}
					</ul>
				</Section>
			</div>
			<Section className="filter-section">
				<Paragraph eyebrow>Amenities</Paragraph>
				<ul className="checkbox-group">
					{filterOptions.vehicleUnitAmenities.map(
						(amenity, index) => {
							const isChecked =
								chosenFilterValues[amenity.checkboxQueryParam];

							const paramName = amenity.checkboxQueryParam;
							const convertedAmenityTitle =
								vehicleFilterToFacetName[paramName] ||
								paramName;

							const noCountAvailable =
								checkFacetCountsForAvailability(
									facetCounts,
									VEHICLE_AMENITIES_LOOKUP,
									convertedAmenityTitle
								);

							const shouldDisableOption =
								noCountAvailable && !isChecked;

							return (
								<li
									key={amenity.checkboxQueryParam}
									className="checkbox-item"
								>
									<Checkbox
										id={`amenity-filter-${index}`}
										label={amenity.label}
										input={{
											value: chosenFilterValues[
												amenity.checkboxQueryParam
											],
											onChange: handleInputChange(
												amenity.checkboxQueryParam
											),
										}}
										disabled={shouldDisableOption}
									/>
								</li>
							);
						}
					)}
				</ul>
			</Section>
		</div>
	);
}

VehicleSearchFilters.propTypes = {
	chosenFilterValues: PropTypes.object,
	handleFilter: PropTypes.func.isRequired,
	handleInputChange: PropTypes.func.isRequired,
	filterOptions: PropTypes.object,
	facetCounts: PropTypes.object,
	checkFacetCountsForAvailability: PropTypes.func.isRequired,
	useNewSearch: PropTypes.bool,
};

export default enhance(VehicleSearchFilters);
